import { IconDownload, IconExternalLink } from "@tabler/icons-react";
import * as _ from "lodash-es";
import { useMemo } from "react";
import { useFileOrigins } from "../../../../hooks/useFileOrigins";
import { useVariantMeters } from "../../../../hooks/useVariantMeters";
import type { Meter } from "../../../../utils/backend-types";
import { EMPTY_FIELD, THEME_VARS } from "../../../../utils/constants";
import { DataImportRequestType } from "../../../../utils/enums";
import { MANY_METERS_TEXT } from "../../../../utils/meter";
import { AnchorLink } from "../../../AnchorLink/AnchorLink";
import { useShouldShowStaffView } from "../../../StaffViewToggle/useShouldShowStaffView";
import type { EnergyDataImportRequest } from "../../EnergyDataUploadFlow/EnergyDataUpload";
import {
  getStatusToRepresentation,
  getTypeOfDataLabel
} from "../../EnergyDataUploadFlow/EnergyDataUpload";
import "./GeneralImportDetailsList.scss";

interface GeneralImportDetailsListProps {
  generalDetails: EnergyDataImportRequest;
}

const generalDetailsTitles = [
  "Hochgeladen durch",
  "Hochgeladen am",
  "Typ",
  "Status",
  "Fehlermeldung",
  "Zähler",
  "Ausgewählte Herkunft"
];

function selectedMeterToRepresentation(
  generalDetails: EnergyDataImportRequest,
  meters: Meter[]
) {
  const selectedMeter = meters.find(
    (meter) => meter.id === generalDetails?.selectedMeter
  );

  if (!generalDetails?.selectedMeter) {
    return MANY_METERS_TEXT;
  } else if (typeof generalDetails?.selectedMeter === "number") {
    return selectedMeter?.name || "";
  }

  return "";
}

function GeneralImportDetailsList({
  generalDetails
}: GeneralImportDetailsListProps) {
  const { origins } = useFileOrigins();
  const { meters } = useVariantMeters(generalDetails?.variant);
  const { shouldShowStaffView } = useShouldShowStaffView();

  const details = useMemo(() => {
    const selectedOrigin = origins.find(
      (origin) => origin.id === generalDetails?.selectedOrigin
    );

    const generalDetailsToRepresentation = [
      generalDetails?.user || "",
      generalDetails?.created || "",
      getTypeOfDataLabel(generalDetails.typeOfData) as string,
      getStatusToRepresentation(generalDetails?.status),
      generalDetails?.errorMessage ||
        `${EMPTY_FIELD} kein Fehler ${EMPTY_FIELD}`,
      ...(generalDetails.typeOfData === DataImportRequestType.REDISPATCH
        ? []
        : [selectedMeterToRepresentation(generalDetails, meters)]),
      generalDetails?.newOriginName || selectedOrigin?.name || EMPTY_FIELD
    ];

    return [
      generalDetails.typeOfData === DataImportRequestType.REDISPATCH
        ? generalDetailsTitles.filter((title) => title !== "Zähler")
        : generalDetailsTitles,
      generalDetailsToRepresentation
    ];
  }, [generalDetails, meters, origins]);

  return (
    <ul className="general-import-details-list">
      {_.unzipWith(details, (title: string, value: string) => (
        <li key={title}>
          <strong>{title}</strong>: {value}
        </li>
      ))}
      <li>
        <strong>Download: </strong>
        <IconDownload color={THEME_VARS.primary[6]} size="1.2em" />

        <AnchorLink
          download
          href={generalDetails?.energyDataFile.dataFile}
          target="_self"
        >
          {generalDetails?.energyDataFile.name}
        </AnchorLink>
      </li>
      {shouldShowStaffView && generalDetails?.adminUrl && (
        <li>
          <strong>Data Import Request: </strong>
          <AnchorLink className="admin-link" href={generalDetails?.adminUrl}>
            Im Admin-Bereich ansehen <IconExternalLink size="1.2em" />
          </AnchorLink>
        </li>
      )}
    </ul>
  );
}

export { GeneralImportDetailsList, GeneralImportDetailsListProps };
